import type React from 'react';
import { Fragment } from 'react';
import type { MouseEvent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { inline } from 'app/shared/styles/_spacing';

// Components
import LinkController from 'app/shared/modules/LinkController';
import ImpressionTracking from 'app/shared/modules/ImpressionTracking';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';

// Misc / Utils
import { NYDOSText } from 'app/shared/modules/listing-cards/ListingCard/styles';
import IconCheckmark from 'images/icons/checkmark-green.svg';
import { shouldShowTotalMonthlyPriceBadge } from 'app/shared/utils/totalMonthlyPriceUtils';

// Lodash
import map from 'lodash/map';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isNull from 'lodash/isNull';
import type { ListingDetails } from 'app/types/listingDetails.type';
import {
  CategoryCell,
  CategorySubcatCell,
  CheckmarkImage,
  ColgroupCategory,
  ColgroupFirstCol,
  ColgroupStandardCol,
  ComparisonTableWrapper,
  DataCell,
  HeaderTitle,
  HeaderTitleContainer,
  HeaderTitleNonLinked,
  HeaderTitleSecondary,
  PlaceholderCell,
  TableBody,
  TableHeader,
  TableHeaderFirstItem,
  TableHeaderPhoto,
  TableHeaderPhotoContainer,
  TableHeaderPhotoPlaceholder,
  TableHeaderStandardItem,
  TablelessHeader,
} from './ComparisonTable.styles';

const { array, bool, func, string } = PropTypes;

interface ListingData {
  rawListingDetails?: ListingDetails;
  link?: string;
  image?: string;
  title?: string;
  [key: string]: string | number | boolean | null | undefined | ListingDetails;
}

interface SharedHeaderProps {
  lazyLoadImage?: boolean;
  title?: string;
  image?: string;
  rawListingDetails?: ListingDetails;
}

interface SharedLinkedHeaderProps extends SharedHeaderProps {
  onClick: (e: MouseEvent<HTMLDivElement>, rawListingDetails?: ListingDetails) => void;
  link?: string;
  trackImpression?: (rawListingDetails?: ListingDetails) => void;
  lazyLoadImage?: boolean;
}

interface ComparisonTableProps {
  categories?: Array<string | Array<string>>;
  className?: string;
  data?: Array<ListingData>;
  hideTable?: boolean;
  lazyLoadImage?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>, rawListingDetails?: ListingDetails) => void;
  placeholder?: string;
  striped?: boolean;
  trackImpression?: (rawListingDetails?: ListingDetails) => void;
  ariaLabel?: string | null;
}

const StyledDiv = styled.div`
  ${inline._1x};
`;

const renderCheckmark = <CheckmarkImage src={IconCheckmark} alt="included" />;

const sharedNonLinkedHeaderMarkup = ({ lazyLoadImage, title, image, rawListingDetails }: SharedHeaderProps) => {
  const shouldDisplayBrokerInfo = Boolean(
    rawListingDetails?.broker?.isBrokerExclusiveListing && rawListingDetails?.broker?.companyName,
  );
  return (
    <Fragment>
      <TableHeaderPhotoContainer>
        {lazyLoadImage ? (
          <TableHeaderPhotoPlaceholder>
            <TableHeaderPhoto altText="" titleText={''} image={image || ''} />
          </TableHeaderPhotoPlaceholder>
        ) : (
          <TableHeaderPhoto altText="" titleText={''} image={image || ''} />
        )}
      </TableHeaderPhotoContainer>
      <HeaderTitleContainer>
        <HeaderTitleSecondary htmlTag="label">{'This listing\n'}</HeaderTitleSecondary>
        <HeaderTitleNonLinked htmlTag="h3">{title}</HeaderTitleNonLinked>
        {shouldDisplayBrokerInfo && rawListingDetails?.broker && (
          <>
            <NYDOSText>{rawListingDetails.broker.companyName}</NYDOSText>
            {rawListingDetails.broker.companyPhoneNumber && (
              <NYDOSText>{rawListingDetails.broker.companyPhoneNumber}</NYDOSText>
            )}
          </>
        )}
      </HeaderTitleContainer>
    </Fragment>
  );
};

const sharedLinkedHeaderMarkup = ({
  onClick,
  rawListingDetails,
  link,
  title,
  image,
  trackImpression,
  lazyLoadImage,
}: SharedLinkedHeaderProps) => {
  const shouldDisplayBrokerInfo = Boolean(
    rawListingDetails?.broker?.isBrokerExclusiveListing && rawListingDetails?.broker?.companyName,
  );
  return (
    <Fragment>
      <div onClick={(e) => onClick(e, rawListingDetails)}>
        <LinkController
          onClick={(e: MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
          }}
          to={link}
        >
          <TableHeaderPhotoContainer>
            {lazyLoadImage ? (
              <TableHeaderPhotoPlaceholder>
                <TableHeaderPhoto altText="" titleText={''} image={image || ''} />
              </TableHeaderPhotoPlaceholder>
            ) : (
              <TableHeaderPhoto altText="" titleText={''} image={image || ''} />
            )}
          </TableHeaderPhotoContainer>
          <HeaderTitleContainer>
            <HeaderTitle htmlTag="h3">{title}</HeaderTitle>
          </HeaderTitleContainer>
          {trackImpression && <ImpressionTracking analyticsCallback={() => trackImpression(rawListingDetails)} />}
        </LinkController>
        {shouldDisplayBrokerInfo && rawListingDetails?.broker && (
          <>
            <NYDOSText>{rawListingDetails.broker.companyName}</NYDOSText>
            {rawListingDetails.broker.companyPhoneNumber && (
              <NYDOSText>{rawListingDetails.broker.companyPhoneNumber}</NYDOSText>
            )}
          </>
        )}
      </div>
    </Fragment>
  );
};

const header = (
  listing: ListingData,
  colIdx: number,
  onClick: (e: MouseEvent<HTMLDivElement>, rawListingDetails?: ListingDetails) => void,
  lazyLoadImage: boolean,
  hideTable?: boolean,
  trackImpression?: (rawListingDetails?: ListingDetails) => void,
) => {
  const { rawListingDetails = {} as ListingDetails, link, image, title } = listing;
  const { aliasEncoded } = rawListingDetails;

  const nonLinkedHeaderItem = hideTable ? (
    <StyledDiv key={`div-${aliasEncoded}-${link}`}>
      {sharedNonLinkedHeaderMarkup({ lazyLoadImage, title, image, rawListingDetails })}
    </StyledDiv>
  ) : (
    <TableHeaderFirstItem key={`th-${aliasEncoded}-${link}`}>
      {sharedNonLinkedHeaderMarkup({ lazyLoadImage, title, image, rawListingDetails })}
    </TableHeaderFirstItem>
  );

  const linkedHeaderItem = hideTable ? (
    <div className={TableHeaderStandardItem} key={`div-${aliasEncoded}-${link}`}>
      {sharedLinkedHeaderMarkup({
        onClick,
        rawListingDetails,
        link,
        title,
        image,
        trackImpression: trackImpression || undefined,
        lazyLoadImage,
      })}
    </div>
  ) : (
    <th className={TableHeaderStandardItem} key={`th-${aliasEncoded}-${link}`}>
      {sharedLinkedHeaderMarkup({
        onClick,
        rawListingDetails,
        link,
        title,
        image,
        trackImpression: trackImpression || undefined,
        lazyLoadImage,
      })}
    </th>
  );

  if (colIdx === 0) {
    return nonLinkedHeaderItem;
  }

  return linkedHeaderItem;
};

const bodyCategoryCell = (category: string | Array<string>, rowIdx: number) => {
  const standardCategoryCell = <div>{category}</div>;

  const categoryWithSubArrayCell = map(category, (subCat, catIdx) => {
    const len = category.length - 1;
    return (
      <CategorySubcatCell
        position={catIdx === 0 ? 'first' : catIdx === len ? 'last' : undefined}
        key={`div-${catIdx}-${subCat}`}
      >
        {subCat}
      </CategorySubcatCell>
    );
  });

  return (
    <CategoryCell firstRow={rowIdx === 0}>
      {isArray(category) && categoryWithSubArrayCell}
      {!isArray(category) && standardCategoryCell}
    </CategoryCell>
  );
};

const bodyCell = (
  category: string | Array<string>,
  item: ListingData,
  placeholder: string,
  rowIdx: number,
  colIdx: number,
) => {
  const standardItemBodyCell = () => {
    const data = item[category as string];
    const hasNoDataPoint = isNull(data) || data === false;

    if (isBoolean(data) && data === true) {
      return renderCheckmark;
    }

    return hasNoDataPoint ? <PlaceholderCell>{placeholder}</PlaceholderCell> : <div>{String(data)}</div>;
  };

  const itemWithSubArrayCell = () => {
    const subCategoryCells = map(category, (subCat, catIdx) => {
      const len = category.length - 1;
      const data = item[subCat];
      const hasNoDataPoint = isNull(data) || data === false;

      if (isBoolean(data) && data === true) {
        return renderCheckmark;
      }

      return (
        <CategorySubcatCell
          position={catIdx === 0 ? 'first' : catIdx === len ? 'last' : undefined}
          key={`div-${rowIdx}-${colIdx}-${catIdx}-${subCat}-${data}`}
        >
          {hasNoDataPoint ? <PlaceholderCell>{placeholder}</PlaceholderCell> : String(data)}
        </CategorySubcatCell>
      );
    });

    // Add TMP badge after the last pricing sub-category if this is the pricing row and listing qualifies
    if (rowIdx === 0 && item.rawListingDetails && shouldShowTotalMonthlyPriceBadge(item.rawListingDetails)) {
      subCategoryCells.push(
        <CategorySubcatCell position="last" key={`div-tmp-badge-${rowIdx}-${colIdx}`}>
          <TotalMonthlyPriceBadge
            listing={item.rawListingDetails}
            iconVariant="default"
            size="sm"
            data-testid={`comparison-table-tmp-badge-${rowIdx}-${colIdx}`}
          />
        </CategorySubcatCell>,
      );
    }

    return subCategoryCells;
  };

  return (
    <DataCell
      firstRow={rowIdx === 0}
      firstCol={colIdx === 0}
      key={`td-${rowIdx}-${colIdx}-${typeof category === 'string' ? category : category.join('-')}-${String(item[category as string])}`}
    >
      {isArray(category) && itemWithSubArrayCell()}
      {!isArray(category) && standardItemBodyCell()}
    </DataCell>
  );
};

const tableRow = (category: string | Array<string>, data: Array<ListingData>, placeholder: string, rowIdx: number) => {
  return (
    <tr key={`tr-${rowIdx}-${category}`}>
      {bodyCategoryCell(category, rowIdx)}
      {map(data, (item, colIdx) => {
        return bodyCell(category, item, placeholder, rowIdx, colIdx);
      })}
    </tr>
  );
};

const ComparisonTable: React.FC<ComparisonTableProps> = ({
  categories = [],
  className = '',
  data = [],
  hideTable = false,
  lazyLoadImage = false,
  onClick = () => {
    // Default no-op handler
  },
  placeholder = '',
  striped = false,
  trackImpression,
  ariaLabel = null,
}) => {
  const generateColgroup = (
    <colgroup>
      <ColgroupCategory key="category-col" />
      {map(data, (_, colIdx) => {
        if (colIdx === 0) {
          return <ColgroupFirstCol key={`col-first-${colIdx}`} />;
        }

        return <ColgroupStandardCol key={`col-standard-${colIdx}`} />;
      })}
    </colgroup>
  );

  const generateHeader = (
    <TableHeader>
      <tr>
        <TableHeaderFirstItem>{/* INTENTIONALLY BLANK */}</TableHeaderFirstItem>
        {map(data, (listing, colIdx) => {
          return header(listing, colIdx, onClick, lazyLoadImage, hideTable, trackImpression);
        })}
      </tr>
    </TableHeader>
  );

  const generateBody = (
    <TableBody striped={striped}>
      {map(categories, (category, rowIdx) => {
        return tableRow(category, data, placeholder, rowIdx);
      })}
    </TableBody>
  );

  return (
    <Fragment>
      {hideTable ? (
        <TablelessHeader>
          {map(data, (listing, colIdx) => {
            return header(listing, colIdx, onClick, lazyLoadImage, hideTable, trackImpression);
          })}
        </TablelessHeader>
      ) : (
        <ComparisonTableWrapper className={className} aria-label={ariaLabel || undefined}>
          {generateColgroup}
          {generateHeader}
          {generateBody}
        </ComparisonTableWrapper>
      )}
    </Fragment>
  );
};

export default ComparisonTable;

ComparisonTable.propTypes = {
  categories: array,
  className: string,
  data: array,
  hideTable: bool,
  lazyLoadImage: bool,
  onClick: func,
  placeholder: string,
  striped: bool,
  trackImpression: func,
  ariaLabel: string,
};
